/**
 * EgoNetworkCanvas - Container for network visualization
 *
 * Provides mode toggle (concentric / force-directed) and renders
 * the appropriate layout component. Also overlays network stats chips.
 */

import React from 'react';
import { Box, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import {
  BubbleChart as ConcentricIcon,
  Grain as ForceIcon,
} from '@mui/icons-material';
import ConcentricLayout from './ConcentricLayout';
import ForceLayout from './ForceLayout';
import NetworkStats from './NetworkStats';
import { VIZ_MODES } from '../../constants/dashboardConfig';

function EgoNetworkCanvas({
  nodes,
  edges,
  egoId,
  networkStats,
  selectedNode,
  selectedEdge,
  vizMode,
  onVizModeChange,
  onNodeSelect,
  onEdgeSelect,
  onDeselect,
}) {
  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      onVizModeChange(newMode);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Mode Toggle - top left */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 20,
        }}
      >
        <ToggleButtonGroup
          value={vizMode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(8px)',
            '& .MuiToggleButton-root': {
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              textTransform: 'none',
            },
          }}
        >
          <ToggleButton value={VIZ_MODES.CONCENTRIC}>
            <Tooltip title="Concentric ring layout">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ConcentricIcon fontSize="small" />
                Zones
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value={VIZ_MODES.FORCE}>
            <Tooltip title="Force-directed layout">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ForceIcon fontSize="small" />
                Force
              </Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Network Stats - top right */}
      <NetworkStats stats={networkStats} />

      {/* Visualization */}
      {vizMode === VIZ_MODES.CONCENTRIC ? (
        <ConcentricLayout
          nodes={nodes}
          edges={edges}
          egoId={egoId}
          selectedNode={selectedNode}
          selectedEdge={selectedEdge}
          onNodeSelect={onNodeSelect}
          onEdgeSelect={onEdgeSelect}
          onDeselect={onDeselect}
        />
      ) : (
        <ForceLayout
          nodes={nodes}
          edges={edges}
          egoId={egoId}
          selectedNode={selectedNode}
          selectedEdge={selectedEdge}
          onNodeSelect={onNodeSelect}
          onEdgeSelect={onEdgeSelect}
          onDeselect={onDeselect}
        />
      )}
    </Box>
  );
}

export default EgoNetworkCanvas;
